import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx/xlsx.mjs';

const TABLE_TYPE = {
  BILLING: 'BILLING',
  POLICY: 'POLICY',
  CLAIMS: 'CLAIMS',
} as const;

type TableType = typeof TABLE_TYPE[keyof typeof TABLE_TYPE];

// Define our own interfaces for XLSX types since they're not exported from xlsx/xlsx.mjs
interface WorkBook {
  SheetNames: string[];
  Sheets: { [key: string]: WorkSheet };
  Props?: {
    Title?: string;
    Subject?: string;
    Author?: string;
    CreatedDate?: Date;
  };
}

interface WorkSheet {
  [key: string]: any;
  '!merges'?: any[];
  '!ref'?: string;
  '!cols'?: ColumnWidth[];
}

interface BillingData {
  AgencyCode: string;
  AgencyName: string;
  PolicyNumber: string;
  InsuredName: string;
  ActivityType: string;
  ActivityDate: string;
  MinimumDue: string;
  Id?: string;
  PolicyHref?: string;
  ClaimNumberName?: string;
}

interface PolicyData {
  AgencyName: string;
  AgencyCode: string;
  PolicyNumber: string;
  InsuredName: string;
  ActivityDate: string;
  TransactionType: string;
  Pages: string;
  Id?: string;
  PolicyHref?: string;
  ClaimNumberName?: string;
}

interface ClaimsData {
  AgencyName: string;
  AgencyCode: string;
  PolicyNumber: string;
  ClaimNumber: string;
  InsuredName: string;
  ActivityDate: string;
  ActivityType: string;
  Id?: string;
  PolicyHref?: string;
  ClaimNumberName?: string;
}

type ExportData = BillingData | PolicyData | ClaimsData;

interface CellFont {
  name?: string;
  bold?: boolean;
  sz?: string;
  color?: { rgb: string };
}

interface CellFill {
  patternType: string;
  bgColor: { rgb: string };
  fgColor: { rgb: string };
}

interface CellStyle {
  font?: CellFont;
  fill?: CellFill;
}

interface ExcelCell {
  v: string;
  s: CellStyle;
}

interface ColumnWidth {
  wch: number;
}

class ExportRecentActivitiesUtility {
  private static Instance: ExportRecentActivitiesUtility | null = null;
  private billingData: BillingData[] = [];
  private policyData: PolicyData[] = [];
  private claimsData: ClaimsData[] = [];
  private readonly DEFAULT_MESSAGE = 'No records were found.';

  static getInstance(): ExportRecentActivitiesUtility {
    if (ExportRecentActivitiesUtility.Instance == null)
      ExportRecentActivitiesUtility.Instance = new ExportRecentActivitiesUtility();

    return ExportRecentActivitiesUtility.Instance;
  }

  ExportData(billingData: BillingData[], policyData: PolicyData[], claimsData: ClaimsData[]): void {
    this.billingData = this.FormatExportedData(billingData) as BillingData[];
    this.policyData = this.FormatExportedData(policyData) as PolicyData[];
    this.claimsData = this.FormatExportedData(claimsData) as ClaimsData[];

    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';
    const fileName = 'Recent Activity';

    const date = new Date();
    const today =
      (date.getMonth() + 1).toString() + '/' + date.getDate().toString() + '/' + date.getFullYear().toString();

    const title = 'Recent Activities on ' + today + ' from Liberty Mutual Insurance®';

    const workbook = XLSX.utils.book_new();

    workbook.Props = {
      Title: title,
      Subject: 'Recent Activities',
      Author: 'Liberty Mutual Insurance',
      CreatedDate: date,
    };

    this.ExportBillingTableData(title, workbook);
    this.ExportPolicyTableData(title, workbook);
    this.ExportClaimsTableData(title, workbook);

    const writeOptions = { bookType: 'xlsx' as const, bookSST: false, type: 'binary' as const, cellStyles: true };
    const excelBuffer = XLSX.write(workbook, writeOptions);

    const data = new Blob([this.s2ab(excelBuffer)], { type: fileType });
    FileSaver.saveAs(data, fileName + fileExtension);
  }

  //function from documentation https://www.npmjs.com/package/js-xlsx
  private s2ab(s: string): ArrayBuffer {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
    return buf;
  }

  private GetMaxLength(mappedFunctionLength: number[], header: string): number {
    return Math.max.apply(Math, mappedFunctionLength) > header.length
      ? Math.max.apply(Math, mappedFunctionLength)
      : header.length;
  }

  private PopulateWorkbook(workbook: WorkBook, tableType: TableType, worksheet: WorkSheet): void {
    workbook.SheetNames.push(tableType);
    workbook.Sheets[tableType] = worksheet;
  }

  private ExportBillingTableData(title: string, workbook: WorkBook): void {
    let billingWs: WorkSheet = {};
    billingWs = this.ConfigureWorkSheetHeaders(billingWs, title, TABLE_TYPE.BILLING);

    this.PopulateWorkbook(workbook, TABLE_TYPE.BILLING, billingWs);

    this.AppendDataToWorkSheet(this.billingData, TABLE_TYPE.BILLING, workbook);

    const wscols: ColumnWidth[] = [
      {
        wch: this.GetMaxLength(
          this.billingData.map(function (o) {
            return o.AgencyCode.length;
          }),
          'AgencyCode',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.billingData.map(function (o) {
            return o.AgencyName.length;
          }),
          'AgencyName',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.billingData.map(function (o) {
            return o.PolicyNumber.length;
          }),
          'PolicyNumber',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.billingData.map(function (o) {
            return o.InsuredName.length;
          }),
          'InsuredName',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.billingData.map(function (o) {
            return o.ActivityType.length;
          }),
          'ActivityType',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.billingData.map(function (o) {
            return o.ActivityDate.length;
          }),
          'ActivityDate',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.billingData.map(function (o) {
            return o.MinimumDue.length;
          }),
          'MinimumDue',
        ),
      },
    ];
    this.SetTableColumnWidths(workbook, TABLE_TYPE.BILLING, wscols);
  }

  private ExportPolicyTableData(title: string, workbook: WorkBook): void {
    let policyWs: WorkSheet = {};
    policyWs = this.ConfigureWorkSheetHeaders(policyWs, title, TABLE_TYPE.POLICY);

    this.PopulateWorkbook(workbook, TABLE_TYPE.POLICY, policyWs);

    this.AppendDataToWorkSheet(this.policyData, TABLE_TYPE.POLICY, workbook);

    const wscols: ColumnWidth[] = [
      {
        wch: this.GetMaxLength(
          this.policyData.map(function (o) {
            return o.AgencyName.length;
          }),
          'AgencyName',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.policyData.map(function (o) {
            return o.AgencyCode.length;
          }),
          'AgencyCode',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.policyData.map(function (o) {
            return o.PolicyNumber.length;
          }),
          'PolicyNumber',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.policyData.map(function (o) {
            return o.InsuredName.length;
          }),
          'InsuredName',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.policyData.map(function (o) {
            return o.ActivityDate.length;
          }),
          'ActivityDate',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.policyData.map(function (o) {
            return o.TransactionType.length;
          }),
          'TransactionType',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.policyData.map(function (o) {
            return o.Pages.length;
          }),
          'Pages',
        ),
      },
    ];
    this.SetTableColumnWidths(workbook, TABLE_TYPE.POLICY, wscols);
  }

  private ExportClaimsTableData(title: string, workbook: WorkBook): void {
    let claimsWs: WorkSheet = {};
    claimsWs = this.ConfigureWorkSheetHeaders(claimsWs, title, TABLE_TYPE.CLAIMS);

    this.PopulateWorkbook(workbook, TABLE_TYPE.CLAIMS, claimsWs);

    this.AppendDataToWorkSheet(this.claimsData, TABLE_TYPE.CLAIMS, workbook);
    const wscols: ColumnWidth[] = [
      {
        wch: this.GetMaxLength(
          this.claimsData.map(function (o) {
            return o.AgencyName.length;
          }),
          'AgencyName',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.claimsData.map(function (o) {
            return o.AgencyCode.length;
          }),
          'AgencyCode',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.claimsData.map(function (o) {
            return o.PolicyNumber.length;
          }),
          'PolicyNumber',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.claimsData.map(function (o) {
            return o.ClaimNumber.length;
          }),
          'ClaimNumber',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.claimsData.map(function (o) {
            return o.InsuredName.length;
          }),
          'InsuredName',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.claimsData.map(function (o) {
            return o.ActivityDate.length;
          }),
          'ActivityDate',
        ),
      },
      {
        wch: this.GetMaxLength(
          this.claimsData.map(function (o) {
            return o.ActivityType.length;
          }),
          'ActivityType',
        ),
      },
    ];
    this.SetTableColumnWidths(workbook, TABLE_TYPE.CLAIMS, wscols);
  }

  private FormatExportedData = (data: ExportData[]): ExportData[] => {
    //remove id param from state tables meant for render mapping and other undesired properties from displaying
    const newArray = data.map(function (item) {
      delete item.Id;
      delete item.PolicyHref;
      delete item.ClaimNumberName;
      return item;
    });
    return newArray;
  };

  private GenerateCell = (textValue: string, font?: CellFont | null, fill?: CellFill | null): ExcelCell => {
    const cell: ExcelCell = {
      v: textValue,
      s: {},
    };

    if (font !== null && font !== undefined) cell.s.font = font;
    if (fill !== null && fill !== undefined) cell.s.fill = fill;

    return cell;
  };

  private ConfigureWorkSheetHeaders = (worksheet: WorkSheet, headerTitle: string, tableType: TableType): WorkSheet => {
    const cellHeaderFont: CellFont = { name: 'Arial', bold: true, sz: '22' };

    const cellTitleFont: CellFont = { name: 'Arial', bold: true, sz: '14', color: { rgb: 'FFFFFF' } };
    const cellTitleFill: CellFill = { patternType: 'solid', bgColor: { rgb: '606060' }, fgColor: { rgb: '606060' } };

    const cellHeader = this.GenerateCell(headerTitle, cellHeaderFont);
    const cellTitle = this.GenerateCell(tableType + '\t', cellTitleFont, cellTitleFill);

    // s == start, e == end, c == col, r == row
    const mergeCellsRange = [{ s: { c: 0, r: 1 }, e: { c: 6, r: 1 } }];
    worksheet['!merges'] = mergeCellsRange;

    const range = { s: { c: 0, r: 0 }, e: { c: 0, r: 4 } }; //A1:A5

    worksheet['!ref'] = XLSX.utils.encode_range(range);
    worksheet[XLSX.utils.encode_cell({ c: 0, r: 0 })] = cellHeader;
    worksheet[XLSX.utils.encode_cell({ c: 0, r: 1 })] = cellTitle;
    return worksheet;
  };

  private AppendDataToWorkSheet = (data: ExportData[], tableType: TableType, workbook: WorkBook): void => {
    if (data.length > 0) {
      XLSX.utils.sheet_add_json(workbook.Sheets[tableType], data, { skipHeader: false, origin: 'A4' });

      const ws = workbook.Sheets[tableType];
      const headerRange = ['A4', 'B4', 'C4', 'D4', 'E4', 'F4', 'G4'];
      for (let i = 0; i < headerRange.length; i++) {
        if (ws[headerRange[i]]) {
          ws[headerRange[i]].s = {
            font: { bold: true },
          };
        }
      }
    } else {
      const resultMsgFont: CellFont = { bold: true, sz: '14' };
      const resultMsg = this.GenerateCell(this.DEFAULT_MESSAGE, resultMsgFont);
      const sheet = workbook.Sheets[tableType];
      sheet[XLSX.utils.encode_cell({ c: 0, r: 3 })] = resultMsg;
    }
  };

  private SetTableColumnWidths(workbook: WorkBook, tableType: TableType, wscols: ColumnWidth[]): void {
    // reference for col param types: wch >> "characters", wpx >> "pixels"
    const ws = workbook.Sheets[tableType];
    ws['!cols'] = wscols;
  }
}

export default ExportRecentActivitiesUtility;
